import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormAttachmentUploadRequirements } from '../attachments/form-attachment-requirements';
import { GeneralFormData } from '../general-form-data.model';
import { formatTimeForDisplay, parseLocalDate } from '../../../utils/date-time-formatter';
import { FormStep } from '../form-steps.interface';


export class EmployeeInjuryReport {
  id?: string;
  submittedByName: string = '';
  submittedByEmail: string = '';
  submissionDate?: Date;
  typeOfIncident: string = '';
  employeeFirstName: string = '';
  employeeLastName: string = '';
  streetAddress: string = '';
  city: string = '';
  stateProvince: string = '';
  zipPostalCode!: number;
  phone: string = '';
  dateOfHire: Date | null = null;
  craftAndPosition: string = '';
  shiftWorked: string = '';
  supervisorFirstName: string = '';
  supervisorLastName: string = '';
  hoursWorkedPerDay!: number;
  hoursWorkedPerWeek!: number;
  dateOfIncident: Date | null = null;
  timeOfIncident: string = '';
  dateReported: Date | null = null;
  timeReported: string = '';
  incidentReportedToFirstName: string = '';
  incidentReportedToLastName: string = '';
  jobSiteOfInjury: string = '';
  exactLocationOfInjury: string = '';
  equipment1: string = '';
  equipment2: string = '';
  anyDefects: string = '';
  coveredInPreTask: string = '';
  witness1: string = '';
  witness2: string = '';
  taskDescription: string = '';
  injuryDescription: string = '';
  howInjuryOccurred: string = '';
  contributingFactors: string = '';
  ppeRequired: string = '';
  ppeWorn: string = '';
  adequateLighting: string = '';
  injured: string = '';
  bloodExposure: string = '';
  seekMedicalTreatment: string = '';
  natureOfInjury: string = '';
  bodyPartInjured: string = '';
  propertyDamage: string = '';
  othersInvolved: string = '';
  previousSimilarCondition: string = '';
  previousInjuryDescription: string = '';
  preventionSuggestion: string = '';
  employeeStatement: string = '';
  picturesDiagrams: Array<string> = [];
  employeeSignature: Array<string> = [];
  signatureDate: Date | null = null;

  constructor(data?: Partial<EmployeeInjuryReport>) {
    // Set default date to today if not provided
    if (data) {
      Object.assign(this, {
        ...data,
        // Convert strings to numbers
        zipPostalCode: data.zipPostalCode ? Number(data.zipPostalCode) : 0,
        hoursWorkedPerDay: data.hoursWorkedPerDay
          ? Number(data.hoursWorkedPerDay)
          : 0,
        hoursWorkedPerWeek: data.hoursWorkedPerWeek
          ? Number(data.hoursWorkedPerWeek)
          : 0,
        // Date conversions
        submissionDate: data.submissionDate
          ? new Date(data.submissionDate)
          : new Date(),
        dateOfHire: data.dateOfHire ? parseLocalDate(data.dateOfHire as any) : null,
        dateOfIncident: data.dateOfIncident
        ? parseLocalDate(data.dateOfIncident as any) : null,
        dateReported: data.dateReported ? parseLocalDate(data.dateReported as any) : null,
        signatureDate: data.signatureDate ? parseLocalDate(data.signatureDate as any) : null,
        //convert time
        timeOfIncident: data.timeOfIncident ? formatTimeForDisplay(data.timeOfIncident) : '',
        timeReported: data.timeReported ? formatTimeForDisplay(data.timeReported) : '',
    
      });
    }
  }

  static getFormSteps(): FormStep[] {
    return [
      {
        id: 'employee-info',
        shortTitle: 'Employee Info',
        title: 'Employee Information',
        icon: 'person-outline',
        description: 'Enter the employee\'s personal information including name, address, and contact details',
        sections: [
          {
            fields: ['employeeFirstName', 'employeeLastName', 'streetAddress', 'city', 'stateProvince', 'zipPostalCode', 'phone', 'dateOfHire', 'craftAndPosition']
          }
        ]
      },
      {
        id: 'job-info',
        shortTitle: 'Job Info',
        title: 'Job Information',
        icon: 'business-outline',
        description: 'Enter the employee\'s job details including position, supervisor, and work hours',
        sections: [
          {
            fields: ['shiftWorked', 'hoursWorkedPerDay', 'hoursWorkedPerWeek', 'supervisorFirstName', 'supervisorLastName']
          }
        ]
      },
      {
        id: 'incident-info',
        shortTitle: 'Incident Info',
        title: 'Incident Information',
        icon: 'alert-circle-outline',
        description: 'Enter details about when and where the incident occurred, and describe what happened',
        sections: [
          {
            fields: ['typeOfIncident', 'dateOfIncident', 'timeOfIncident', 'dateReported', 'timeReported', 'incidentReportedToFirstName', 'incidentReportedToLastName', 'jobSiteOfInjury', 'exactLocationOfInjury', 'witness1', 'witness2', 'equipment1', 'equipment2', 'anyDefects', 'coveredInPreTask', 'taskDescription', 'howInjuryOccurred', 'contributingFactors']
          }
        ]
      },
      {
        id: 'injury-info',
        shortTitle: 'Injury Info',
        title: 'Injury Information',
        icon: 'medkit-outline',
        description: 'Enter details about the injury, medical treatment, and prevention suggestions',
        sections: [
          {
            fields: ['injured', 'natureOfInjury', 'bodyPartInjured', 'injuryDescription', 'seekMedicalTreatment', 'previousSimilarCondition', 'previousInjuryDescription', 'ppeRequired', 'ppeWorn', 'adequateLighting', 'bloodExposure']
          }
        ]
      },
      {
        id: 'additional-info',
        shortTitle: 'Additional Info',
        title: 'Additional Information',
        icon: 'information-circle-outline',
        description: 'Enter additional information about the injury and submit attachments/signature',
        sections: [
          {
            fields: ['propertyDamage', 'othersInvolved', 'preventionSuggestion', 'employeeStatement','picturesDiagrams', 'signatureDate', 'employeeSignature']
          }
        ]
      }
    ];
  }

  static getFormInfo(): GeneralFormData {
    return {
      formType: 'employee-injury-report',
      formTitle: 'Employee Injury Report',
      formIcon: 'medkit-outline',
      cardImage: 'assets/form-card-tiles/employee-injury-report.png',
      formAttachmentRequirements: {
        uploads: [
          {
            identifier: 'picturesDiagrams',
            label: this.getFieldMappings()['picturesDiagrams'],
            required: 0,
          },
        ],
        signatures: [
          {
            identifier: 'employeeSignature',
            label: this.getFieldMappings()['employeeSignature'],
            required: true,
          },
        ],
      },
    };
  }

  static getFieldMappings(): Record<string, string> {
    return {
      picturesDiagrams: 'Photos/Diagrams',
      employeeSignature: 'Employee Signature',
      submittedByName: 'Submitter Name',
      submittedByEmail: 'Submitter Email',
      submissionDate: 'Submission Date',
      typeOfIncident: 'Type of Incident',
      employeeFirstName: 'Name (First)',
      employeeLastName: 'Name (Last)',
      streetAddress: 'Street Address',
      city: 'City',
      stateProvince: 'State / Province',
      zipPostalCode: 'ZIP / Postal Code',
      phone: 'Phone',
      dateOfHire: 'Date of Hire',
      craftAndPosition: 'Craft and Position',
      shiftWorked: 'Shift Worked',
      supervisorFirstName: 'Supervisor Name (First)',
      supervisorLastName: 'Supervisor Name (Last)',
      hoursWorkedPerDay: 'Hours Worked Per Day',
      hoursWorkedPerWeek: 'Hours Worked Per Week',
      dateOfIncident: 'Date of Incident',
      timeOfIncident: 'Time of Incident',
      dateReported: 'Date Reported',
      timeReported: 'Time Reported',
      incidentReportedToFirstName: 'Incident Reported To (First)',
      incidentReportedToLastName: 'Incident Reported To (Last)',
      jobSiteOfInjury: 'Job site of injury',
      exactLocationOfInjury: 'Exact location where the injury occured',
      equipment1: 'Equipment/Tools Involved #1',
      equipment2: 'Equipment/Tools Involved #2',
      anyDefects: 'Any Defects',
      coveredInPreTask: 'Was the Task/Job Covered on the Pre-Task?',
      witness1: 'Witness Names (fill out Witness Report 8.04) #1',
      witness2: 'Witness Names (fill out Witness Report 8.04) #2',
      taskDescription:
        'Describe in detail the task being performed at the time of the incident. (Step by step)',
      injuryDescription:
        'What Was Your Injury? (Body part and description of injury)',
      howInjuryOccurred: 'How did the injury occur?',
      contributingFactors:
        'What were the Contributing Factors that Lead Up to the Incident?',
      ppeRequired: 'Was Personal Protective Equipment Required?',
      ppeWorn: 'Was Protective Equipment Worn?',
      adequateLighting: 'Was There Adequate Lighting?',
      injured: 'Were You Injured?',
      bloodExposure: "Were You Exposed to Another Person's Blood?",
      seekMedicalTreatment:
        'Will You Seek Medical Treatment? (Beyond On-Site Medic, for instance On-Site Health & Safety)',
      natureOfInjury: 'Nature of Injury',
      bodyPartInjured: 'Part of Body Injured',
      propertyDamage: 'Was There Property Damage?',
      othersInvolved: 'Were Other Persons or Objects Involved?',
      previousSimilarCondition:
        'Have You Been Treated From Something Similar to this Condition Before?',
      previousInjuryDescription: 'If So, Please Describe the Previous Injury',
      preventionSuggestion: 'How Could this Incident Have Been Avoided?',
      employeeStatement: 'Additional/Employee Statement',
   
      signatureDate: 'Signature Date',
    };
  }

}
